import React from 'react';
import { StopCircle } from 'lucide-react';
import { MessageContentRenderer } from '../MessageContent';
import { MessageActions } from './MessageActions';
import { InvestigationReportGenerationLoader } from '../MessageContent/InvestigationReportGenerationLoader';
import { MessageContentLookupProvider } from '../MessageContent/MessageContentLookupProvider';
import { ToolUseGroup } from '../MessageContent/ToolUseGroup';
import {
  type Message as MessageType,
  type ContentBlock,
  type ToolUseBlock,
} from '../../store/chatStore';

import { useTranslation } from 'react-i18next';

/**
 * Tools that have dedicated rich renderers and should NOT be collapsed into a
 * ToolUseGroup. Everything not in this set renders as generic ToolUseContent
 * and is eligible for grouping.
 */
const UNGROUPABLE_TOOLS = new Set([
  // Rich content renderers
  'GetDashboard',
  'ManageDashboard',
  'GenerateReport',
  'RenderHTML',
  // File / shell ops
  'writeFiles',
  'shareFile',
  'printFile',
  'listFiles',
  'copyFile',
  'deleteFile',
  'readFile',
  'editFile',
  'shellRun',
  // Skill management
  'listPleriSkills',
  'createPleriSkill',
  'updatePleriSkill',
  'deletePleriSkill',
  'forkTemplate',
  // Credentials
  'shellAssumeRole',
  'shellListAssumableRoles',
  // UX / flow tools
  'askQuestion',
  'presentPlan',
  'updatePlanProgress',
  // Investigation (has special loader injection logic in Message.tsx)
  'analyzeExploitability',
  // Legacy names
  'cloudShellRun',
  'cloudShellWriteFiles',
  'cloudShellReadFile',
  'cloudShellEditFile',
  'cloudShellListFiles',
  'cloudShellGetDownloadLink',
  'cloudShellPrint',
  'cloudShellGlob',
  'cloudShellPublish',
  'workspaceWriteFiles',
  'workspaceReadFile',
  'workspaceEditFile',
  'workspaceListFiles',
  'workspaceDeleteFile',
  'workspaceGetDownloadLink',
  'workspacePrint',
  'stageSkillForShell',
  'stageFileForShell',
]);

function isGroupable(block: ContentBlock): block is ToolUseBlock {
  return block.type === 'toolUse' && !UNGROUPABLE_TOOLS.has(block.name);
}

/**
 * Blocks that render nothing (toolResult → always null; `<empty/>` text → invisible
 * sentinel the backend emits between tool calls). Filtering these out before grouping
 * allows consecutive pill-style tool-use blocks to be collapsed even when the backend
 * interleaves them with results/sentinels.
 */
function isTransparentBlock(block: ContentBlock): boolean {
  if (block.type === 'toolResult') return true;
  if (block.type === 'text' && block.text === '<empty/>') return true;
  return false;
}

type ContentItem =
  | { kind: 'block'; block: ContentBlock; originalIndex: number }
  | { kind: 'group'; tools: ToolUseBlock[]; startIndex: number };

function groupToolUseBlocks(blocks: ContentBlock[]): ContentItem[] {
  const result: ContentItem[] = [];
  let run: { tool: ToolUseBlock; index: number }[] = [];

  const flushRun = () => {
    if (run.length >= 2) {
      result.push({
        kind: 'group',
        tools: run.map((r) => r.tool),
        startIndex: run[0].index,
      });
    } else if (run.length === 1) {
      result.push({
        kind: 'block',
        block: run[0].tool,
        originalIndex: run[0].index,
      });
    }
    run = [];
  };

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    if (isGroupable(block)) {
      run.push({ tool: block, index: i });
    } else {
      flushRun();
      result.push({ kind: 'block', block, originalIndex: i });
    }
  }
  flushRun();

  return result;
}

/**
 * Tools that render as rich visual blocks (terminal, dashboard, file editor…).
 * These are NOT connected by the stepper line — their heights and layouts vary
 * too much to align with the icon-column connector at left: 19px.
 * The AgentActivityPill tools (loadTools / loadSkill / getSkillResource) are
 * intentionally absent here so they DO get connected.
 */
const RICH_VISUAL_TOOLS = new Set([
  'GetDashboard',
  'ManageDashboard',
  'GenerateReport',
  'RenderHTML',
  'writeFiles',
  'shareFile',
  'printFile',
  'listFiles',
  'copyFile',
  'deleteFile',
  'readFile',
  'editFile',
  'shellRun',
  'listPleriSkills',
  'createPleriSkill',
  'updatePleriSkill',
  'deletePleriSkill',
  'forkTemplate',
  'shellAssumeRole',
  'shellListAssumableRoles',
  'askQuestion',
  'presentPlan',
  'updatePlanProgress',
  'analyzeExploitability',
  'cloudShellRun',
  'cloudShellWriteFiles',
  'cloudShellReadFile',
  'cloudShellEditFile',
  'cloudShellListFiles',
  'cloudShellGetDownloadLink',
  'cloudShellPrint',
  'cloudShellGlob',
  'cloudShellPublish',
  'workspaceWriteFiles',
  'workspaceReadFile',
  'workspaceEditFile',
  'workspaceListFiles',
  'workspaceDeleteFile',
  'workspaceGetDownloadLink',
  'workspacePrint',
  'stageSkillForShell',
  'stageFileForShell',
]);

/** Returns true for compact pill-style items that should be linked by the
 *  stepper connector line (ToolUseGroup, generic ToolUseContent, AgentActivityPill). */
function isConnectableToolItem(item: ContentItem): boolean {
  if (item.kind === 'group') {
    return true;
  }
  const { block } = item;
  if (block.type !== 'toolUse' && block.type !== 'toolCallChunk') {
    return false;
  }
  const name = block.type === 'toolUse' ? block.name : '';
  return !RICH_VISUAL_TOOLS.has(name);
}

/**
 * Positional key for all content blocks.
 *
 * When a toolCallChunk at position N is replaced by a toolUse at the same
 * position, the key stays the same → React updates in place instead of
 * unmounting/remounting (which causes a visible flash).
 */
function contentKey(_block: ContentBlock, index: number): string {
  return `${index}`;
}

interface MessageProps {
  index: number;
  message: MessageType;
  isLoading: boolean;
  onToolExpand?: () => void;
  /**
   * If true, the tool use block will not collapse when the tool is expanded.
   * This is useful for the chat area in the small chat window.
   */
  preventToolUseBlockToCollapse?: boolean;
  isLastMessage: boolean;
  isReadOnly?: boolean;
}

export const Message: React.FC<MessageProps> = React.memo(
  ({
    index,
    message,
    isLoading,
    onToolExpand,
    isLastMessage,
    preventToolUseBlockToCollapse,
    isReadOnly,
  }: MessageProps) => {
    const { t } = useTranslation('pleri/chat');
    // Order content so that file-like items are first, text items last
    const orderedContent = React.useMemo<ContentBlock[]>(() => {
      const isFileOrImage = (c: (typeof message.content)[number]): boolean =>
        c.type === 'file' ||
        c.type === 'image' ||
        c.type === 'image_url' ||
        (c.type === 'text' && c.metadata?.contentType === 'file');

      const top: typeof message.content = [];
      const rest: typeof message.content = [];
      for (const item of message.content) {
        if (isFileOrImage(item)) {
          top.push(item);
        } else {
          rest.push(item);
        }
      }
      return [...top, ...rest];
    }, [message]);

    // Strip transparent blocks (toolResult → renders null; `<empty/>` sentinel
    // text → invisible) before grouping so they don't break consecutive pill runs.
    const visibleContent = React.useMemo(
      () => orderedContent.filter((b) => !isTransparentBlock(b)),
      [orderedContent],
    );

    const groupedContent = React.useMemo(
      () => groupToolUseBlocks(visibleContent),
      [visibleContent],
    );

    const hasInvestigateSuccessResult = React.useMemo(
      () =>
        message.content.some(
          (block) =>
            block.type === 'toolResult' &&
            block.toolName === 'analyzeExploitability' &&
            block.isError === false,
        ),
      [message.content],
    );

    const hasInvestigateErrorResult = React.useMemo(
      () =>
        message.content.some(
          (block) =>
            block.type === 'toolResult' &&
            block.toolName === 'analyzeExploitability' &&
            block.isError === true,
        ),
      [message.content],
    );

    const investigateToolUseIndex = React.useMemo(
      () =>
        visibleContent.findIndex(
          (block) =>
            block.type === 'toolUse' && block.name === 'analyzeExploitability',
        ),
      [visibleContent],
    );

    const firstVulnerabilityMarkdownIndex = React.useMemo(
      () =>
        visibleContent.findIndex(
          (block) =>
            block.type === 'text' &&
            /```vulnerabilityInvestigation\b/.test(block.text),
        ),
      [visibleContent],
    );

    const shouldShowInvestigationGenerationLoader =
      message.role === 'assistant' &&
      isLastMessage &&
      isLoading &&
      !hasInvestigateErrorResult &&
      (hasInvestigateSuccessResult || firstVulnerabilityMarkdownIndex !== -1);

    const loaderInsertIndex =
      firstVulnerabilityMarkdownIndex !== -1
        ? firstVulnerabilityMarkdownIndex
        : investigateToolUseIndex !== -1
          ? investigateToolUseIndex + 1
          : visibleContent.length;

    return (
      <div className="group mb-6">
        <div
          className={`flex ${message.role === 'user' ? 'ml-20 justify-end' : 'justify-start'}`}
        >
          <div
            className={`relative ${
              message.role === 'user'
                ? 'max-w-full rounded-2xl border border-gray-300 bg-gray-50 text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
                : 'w-full text-gray-800 dark:text-gray-200'
            }`}
          >
            <MessageContentLookupProvider content={message.content}>
              <div
                className={`flex flex-col gap-2 ${message.role === 'user' ? 'px-3 py-2' : ''}`}
              >
                {groupedContent.map((item, itemIndex) => {
                  const nextItem = groupedContent[itemIndex + 1];
                  const connector =
                    isConnectableToolItem(item) &&
                    !!nextItem &&
                    isConnectableToolItem(nextItem) ? (
                      // Negative margins cancel the flex gap-2 so the line
                      // snaps tight to each adjacent pill.
                      <div
                        className="w-px bg-gradient-to-b from-gray-400/80 via-gray-400/40 to-transparent dark:from-gray-500/70 dark:via-gray-500/25 dark:to-transparent"
                        style={{
                          height: 16,
                          marginLeft: 19,
                          marginTop: -8,
                          marginBottom: -8,
                        }}
                      />
                    ) : null;

                  if (item.kind === 'group') {
                    return (
                      <React.Fragment key={`group-${item.startIndex}`}>
                        <ToolUseGroup
                          tools={item.tools}
                          onExpand={onToolExpand}
                          preventToolUseBlockToCollapse={
                            preventToolUseBlockToCollapse
                          }
                        />
                        {connector}
                      </React.Fragment>
                    );
                  }
                  return (
                    <React.Fragment
                      key={contentKey(item.block, item.originalIndex)}
                    >
                      {shouldShowInvestigationGenerationLoader &&
                        item.originalIndex === loaderInsertIndex && (
                          <InvestigationReportGenerationLoader />
                        )}
                      <MessageContentRenderer
                        content={item.block}
                        role={
                          message.role === 'system' ? 'assistant' : message.role
                        }
                        messageId={message.id}
                        onToolExpand={onToolExpand}
                        isLastMessage={isLastMessage}
                        isLastContent={
                          item.originalIndex === visibleContent.length - 1
                        }
                        isLoading={isLoading}
                        preventToolUseBlockToCollapse={
                          preventToolUseBlockToCollapse
                        }
                      />
                      {connector}
                    </React.Fragment>
                  );
                })}
                {shouldShowInvestigationGenerationLoader &&
                  loaderInsertIndex === visibleContent.length && (
                    <InvestigationReportGenerationLoader />
                  )}
              </div>
            </MessageContentLookupProvider>

            {message.role === 'assistant' && (
              <MessageActions
                index={index}
                messageId={message.id}
                isLastMessage={isLastMessage}
                isLoading={isLoading}
                isReadOnly={isReadOnly}
              />
            )}

            {message.role === 'assistant' && message.meta?.stopped && (
              <div className="mt-1 px-1 pb-1">
                <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                  <StopCircle className="h-3 w-3" />
                  {t('stopped')}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
);

Message.displayName = 'Message';
